import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { handleResponse } from '@/api/handleResponse';
// Type-only import — fully elided at build, so the `.server` runtime module is
// never pulled into the browser bundle.
import type {
    Client,
    EtchoManagedClientToSave,
    EtchoManagedClientToUpdate,
    IntegrationManagedClientToSave,
} from '@/models/client.server';

export type {
    Client,
    EtchoManagedClientToSave,
    EtchoManagedClientToUpdate,
    IntegrationManagedClientToSave,
};

/**
 * Browser port of `fetchClients` from app/models/client.server.ts. The token,
 * base URL and JSON headers are handled by `apiFetch`.
 */
export async function fetchClients() {
    const response = await apiFetch('/clients', { method: 'GET' });
    return handleResponse<Client[]>(response, 'GET /clients');
}

/** Browser port of `fetchClientById`. */
export async function fetchClientById(clientId: string) {
    const response = await apiFetch(`/clients/${clientId}`, { method: 'GET' });
    return handleResponse<Client>(response, `GET /clients/${clientId}`);
}

export const clientQueryKey = (clientId: string) =>
    ['client', clientId] as const;

/** Single client by id — replaces the `$clientId` route loader. */
export function useClient(clientId: string | undefined) {
    return useQuery({
        queryKey: clientQueryKey(clientId ?? ''),
        enabled: !!clientId,
        queryFn: async () => {
            const { data, error } = await fetchClientById(clientId as string);
            if (error) throw new Error(error);
            return data;
        },
    });
}

/** Browser port of `saveClient` (POST /clients). */
export async function saveClient(client: EtchoManagedClientToSave) {
    const response = await apiFetch('/clients', { method: 'POST', body: client });
    return handleResponse<Client>(response, 'POST /clients');
}

/** Browser port of `updateClient` (PATCH /clients/:id). */
export async function updateClient(
    clientId: string,
    client: EtchoManagedClientToUpdate
) {
    const response = await apiFetch(`/clients/${clientId}`, {
        method: 'PATCH',
        body: client,
    });
    return handleResponse<Client>(response, `PATCH /clients/${clientId}`);
}

/** Browser port of `deleteClientById` (DELETE /clients/:id). */
export async function deleteClientById(clientId: string) {
    const response = await apiFetch(`/clients/${clientId}`, { method: 'DELETE' });
    return handleResponse<null>(response, `DELETE /clients/${clientId}`);
}

/** Browser port of `resyncClientById` (PUT /clients/:id/resync). */
export async function resyncClientById(clientId: string) {
    const response = await apiFetch(`/clients/${clientId}/resync`, {
        method: 'PUT',
    });
    return handleResponse<null>(response, `PUT /clients/${clientId}/resync`);
}

/** Browser port of `createIntegrationClients` (POST /clients-collection). */
export async function createIntegrationClients(
    clients: IntegrationManagedClientToSave[]
) {
    const response = await apiFetch('/clients-collection', {
        method: 'POST',
        body: clients,
    });
    return handleResponse<Client[]>(response, 'POST /clients-collection');
}

export const clientsQueryKey = ['clients'] as const;

/** TanStack Query hook replacing the old route loader for the clients list. */
export function useClients() {
    return useQuery({
        queryKey: clientsQueryKey,
        queryFn: async () => {
            const { data, error } = await fetchClients();
            if (error) throw new Error(error);
            return data ?? [];
        },
    });
}
